import { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '../ui/Button';
import { NotificationCenter } from './NotificationCenter';
import { smartNotificationService } from '@/services/intelligentScheduling/SmartNotificationService';
import { InAppNotification } from '@/types/intelligentScheduling';

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasNewNotification, setHasNewNotification] = useState(false);

  useEffect(() => {
    // Load initial unread count
    const updateUnreadCount = () => {
      const unread = smartNotificationService.getUnreadNotifications();
      setUnreadCount(unread.length);
    };

    updateUnreadCount();

    // Listen for notification updates
    const handleNotificationUpdate = (event: string, _data: InAppNotification | { notificationId: string }) => {
      switch (event) {
        case 'notification_added':
          setUnreadCount(prev => prev + 1);
          setHasNewNotification(true);
          
          // Clear the "new" indicator after a few seconds
          setTimeout(() => setHasNewNotification(false), 3000);
          break;
          
        case 'notification_updated':
          // Recalculate unread count
          updateUnreadCount();
          break;
          
        case 'notification_removed':
          updateUnreadCount();
          break;
      }
    };

    smartNotificationService.addNotificationListener(handleNotificationUpdate);

    return () => {
      smartNotificationService.removeNotificationListener(handleNotificationUpdate);
    };
  }, []);

  const handleBellClick = () => {
    setIsOpen(true);
    setHasNewNotification(false);
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleBellClick}
        className={`relative transition-all duration-200 ${
          hasNewNotification ? 'animate-bounce' : ''
        }`}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell className={`h-5 w-5 transition-colors ${
          unreadCount > 0 ? 'text-primary' : 'text-muted-foreground'
        }`} />
        
        {/* Unread count badge */}
        {unreadCount > 0 && (
          <div className={`absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center transition-all duration-200 ${
            hasNewNotification ? 'scale-110' : 'scale-100'
          }`}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </div>
        )}
        
        {/* New notification pulse */}
        {hasNewNotification && (
          <div className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full animate-ping" />
        )}
      </Button>

      <NotificationCenter 
        isOpen={isOpen} 
        onClose={() => setIsOpen(false)} 
      />
    </>
  );
}